import Icon from "../../../components/AppIcon";
import Image from "../../../components/AppImage";
import type { AnalyticsFeature } from "../types";

const FeaturesGrid = () => {
  const features: AnalyticsFeature[] = [
    {
      id: "1",
      title: "Footfall Analytics",
      description:
        "Track customer movement patterns, dwell times, and zone popularity with precision heat mapping and real-time occupancy monitoring.",
      icon: "Users",
      metrics: "98.5% accuracy",
      image: "https://images.unsplash.com/photo-1706275299046-2736dec745f0",
      alt: "Retail store interior with customers walking through aisles, overhead view showing foot traffic patterns",
    },
    {
      id: "2",
      title: "Queue Management",
      description:
        "Optimize service efficiency with intelligent queue detection, wait time estimation, and automated staff allocation recommendations.",
      icon: "Clock",
      metrics: "35% reduction in wait times",
      image: "https://images.unsplash.com/photo-1697186216592-b230f0f65209",
      alt: "Bank or retail counter with customers waiting in organized queue line, service desk visible in background",
    },
    {
      id: "3",
      title: "Vehicle Recognition",
      description:
        "Advanced ANPR technology for parking management, traffic flow analysis, and automated access control with 99% plate accuracy.",
      icon: "Car",
      metrics: "99.2% plate recognition",
      image: "https://images.unsplash.com/photo-1704450606472-290bd2ef5d04",
      alt: "Highway traffic with multiple vehicles, license plates clearly visible on cars in motion",
    },
    {
      id: "4",
      title: "Behavior Analysis",
      description:
        "Detect anomalies, identify suspicious activities, and predict potential security incidents before they escalate.",
      icon: "Eye",
      metrics: "Real-time threat detection",
      image: "https://images.unsplash.com/photo-1612043071344-94c20c4c837e",
      alt: "Security monitoring room with multiple screens showing surveillance footage and analytics overlays",
    },
    {
      id: "5",
      title: "Crowd Density Monitoring",
      description:
        "Prevent congestion and maintain safety with real-time crowd analysis, hotspot detection, and automated alerts during high-traffic moments.",
      icon: "Gauge",
      metrics: "Real-time density alerts",
      image: "https://images.unsplash.com/photo-1552664730-d307ca884978",
      alt: "Overhead view of a crowded public area with AI overlays showing density levels and movement patterns",
    },
    {
      id: "6",
      title: "Occupancy Monitoring",
      description:
        "Ensure compliance with capacity limits, optimize space utilization, and maintain safety standards with real-time headcount.",
      icon: "Building",
      metrics: "Instant capacity alerts",
      image: "https://images.unsplash.com/photo-1589779256250-a8743f78f4af",
      alt: "Modern office workspace with employees at desks, open floor plan showing occupancy levels",
    },
  ];

  return (
    <section className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 bg-secondary/10 rounded-full mb-4">
            <Icon name="Zap" size={16} className="text-secondary mr-2" />
            <span className="text-sm font-medium text-secondary">
              50+ Analytics Features
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            See Beyond the Visible
          </h2>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            Transform every camera into an intelligent sensor delivering
            actionable insights for your business
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature) => (
            <div
              key={feature.id}
              className="group bg-card rounded-xl overflow-hidden shadow-brand hover:shadow-brand-lg transition-all duration-300 hover:-translate-y-1"
            >
              <div className="relative h-48 overflow-hidden">
                <Image
                  src={feature.image}
                  alt={feature.alt}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />

                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-4 left-4 flex items-center gap-2 bg-primary/90 px-3 py-1.5 rounded-full">
                  <Icon
                    name={feature.icon}
                    size={16}
                    className="text-primary-foreground"
                  />
                  <span className="text-xs font-medium text-primary-foreground">
                    {feature.metrics}
                  </span>
                </div>
              </div>

              <div className="p-6">
                <h3 className="text-xl font-bold text-foreground mb-3 flex items-center gap-2">
                  <Icon
                    name={feature.icon}
                    size={20}
                    className="text-primary"
                  />
                  {feature.title}
                </h3>
                <p className="text-text-secondary leading-relaxed mb-4">
                  {feature.description}
                </p>
                {/* <button className="text-primary font-medium flex items-center gap-2 hover:gap-3 transition-all duration-300">
                  Learn More
                  <Icon name="ArrowRight" size={16} />
                </button> */}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturesGrid;
